/**
 * Pict-Provider-AuthClient
 * ------------------------
 * Browser-side wrapper around the orator-authentication endpoints. Keeps the
 * current login state on AppData.Auth and exposes promise-returning helpers
 * for the views to call. No DOM manipulation lives here — that's the view's
 * job. Views read AppData.Auth.LoggedIn / AppData.Auth.User to render.
 *
 * AppData shape this provider owns:
 *   AppData.Auth = {
 *       LoggedIn:    false,
 *       User:        null,                  // { IDUser, Email, DisplayName, LoginID }
 *       Providers:   [],                    // [{ Name, Type, BeginURL }, …] from /OAuth/Providers
 *       LastError:   null,                  // most recent error message (cleared on success)
 *       Checking:    false                  // true while a CheckSession call is in flight
 *   }
 */

const libPictProvider = require('pict').ServiceProviderBase;

const _DefaultProviderConfiguration =
{
	ProviderIdentifier: 'AuthClient',
	Dependencies: [],
	RoutePrefix: '/1.0/'
};

class PictProviderAuthClient extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		let tmpOptions = Object.assign({}, _DefaultProviderConfiguration, pOptions || {});
		super(pFable, tmpOptions, pServiceHash);

		this.routePrefix = this.options.RoutePrefix;
	}

	initializeAppData()
	{
		if (!this.fable.AppData.Auth)
		{
			this.fable.AppData.Auth =
			{
				LoggedIn: false,
				User: null,
				Providers: [],
				LastError: null,
				Checking: false
			};
		}
	}

	_url(pPath)
	{
		let tmpPrefix = this.routePrefix;
		if (tmpPrefix.charAt(tmpPrefix.length - 1) !== '/') tmpPrefix += '/';
		return tmpPrefix + pPath.replace(/^\//, '');
	}

	_fetchJSON(pPath, pOptions)
	{
		let tmpOptions = Object.assign({ credentials: 'same-origin' }, pOptions || {});
		tmpOptions.headers = Object.assign(
			{ 'Accept': 'application/json' },
			tmpOptions.headers || {});
		return fetch(this._url(pPath), tmpOptions).then((pResp) =>
		{
			if (!pResp.ok && pResp.status !== 401)
			{
				return pResp.text().then((pText) =>
				{
					throw new Error(`HTTP ${pResp.status}: ${pText || pResp.statusText}`);
				});
			}
			return pResp.json();
		});
	}

	/**
	 * Confirm whether the cookie still names a valid session. Called on app
	 * load; updates AppData.Auth in place.
	 */
	checkSession()
	{
		this.initializeAppData();
		this.fable.AppData.Auth.Checking = true;
		return this._fetchJSON('CheckSession')
			.then((pBody) =>
			{
				this.fable.AppData.Auth.Checking = false;
				if (pBody && pBody.LoggedIn && pBody.UserRecord)
				{
					this.fable.AppData.Auth.LoggedIn = true;
					this.fable.AppData.Auth.User = pBody.UserRecord;
				}
				else
				{
					this.fable.AppData.Auth.LoggedIn = false;
					this.fable.AppData.Auth.User = null;
				}
				return this.fable.AppData.Auth.LoggedIn;
			})
			.catch((pErr) =>
			{
				this.fable.AppData.Auth.Checking = false;
				this.fable.AppData.Auth.LoggedIn = false;
				this.fable.AppData.Auth.User = null;
				this.fable.AppData.Auth.LastError = pErr.message;
				return false;
			});
	}

	loadProviders()
	{
		this.initializeAppData();
		return this._fetchJSON('OAuth/Providers')
			.then((pBody) =>
			{
				this.fable.AppData.Auth.Providers = (pBody && Array.isArray(pBody.Providers)) ? pBody.Providers : [];
				return this.fable.AppData.Auth.Providers;
			})
			.catch(() =>
			{
				this.fable.AppData.Auth.Providers = [];
				return [];
			});
	}

	login(pUsername, pPassword)
	{
		this.initializeAppData();
		this.fable.AppData.Auth.LastError = null;
		this.fable.AppData.Auth.PendingChallenge = null;
		return this._fetchJSON('Login',
			{
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ UserName: pUsername, Password: pPassword })
			})
			.then((pBody) =>
			{
				if (pBody && pBody.LoggedIn && pBody.UserRecord)
				{
					this.fable.AppData.Auth.LoggedIn = true;
					this.fable.AppData.Auth.User = pBody.UserRecord;
					// Restricted session: an org requires 2FA and this account is not
					// enrolled. Signed in, but gated to enrollment until it turns 2FA on.
					if (pBody.MustEnroll2FA || pBody.UserRecord.MustEnroll2FA) { return 'enroll'; }
					return true;
				}
				if (pBody && pBody.Challenge && pBody.ChallengeToken)
				{
					this.fable.AppData.Auth.PendingChallenge =
					{
						ChallengeToken: pBody.ChallengeToken,
						Channel: pBody.Channel || 'email',
						MaskedDestination: pBody.MaskedDestination || '',
						ExpiresInSeconds: pBody.ExpiresInSeconds || 0
					};
					return 'challenge';
				}
				this.fable.AppData.Auth.LastError = (pBody && pBody.Error) || 'Authentication failed.';
				return false;
			})
			.catch((pErr) =>
			{
				this.fable.AppData.Auth.LastError = pErr.message;
				return false;
			});
	}

	// Step 2 of two-step login: submit the emailed/texted code against the pending
	// challenge. Resolves true (signed in) or false (LastError set).
	verifyLoginCode(pCode)
	{
		this.initializeAppData();
		this.fable.AppData.Auth.LastError = null;
		let tmpChallenge = this.fable.AppData.Auth.PendingChallenge;
		if (!tmpChallenge || !tmpChallenge.ChallengeToken)
		{
			this.fable.AppData.Auth.LastError = 'This sign-in request is no longer valid. Please start again.';
			return Promise.resolve(false);
		}
		return this._fetchJSON('Login/Verify',
			{
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ ChallengeToken: tmpChallenge.ChallengeToken, Code: pCode })
			})
			.then((pBody) =>
			{
				if (pBody && pBody.LoggedIn && pBody.UserRecord)
				{
					this.fable.AppData.Auth.LoggedIn = true;
					this.fable.AppData.Auth.User = pBody.UserRecord;
					this.fable.AppData.Auth.PendingChallenge = null;
					return true;
				}
				this.fable.AppData.Auth.LastError = (pBody && pBody.Error) || 'Incorrect code.';
				return false;
			})
			.catch((pErr) =>
			{
				this.fable.AppData.Auth.LastError = pErr.message;
				return false;
			});
	}

	// Stand in a one-time recovery code for the emailed/texted code against the
	// pending challenge. Resolves true (signed in) or false (LastError set).
	useRecoveryCode(pCode)
	{
		this.initializeAppData();
		this.fable.AppData.Auth.LastError = null;
		let tmpChallenge = this.fable.AppData.Auth.PendingChallenge;
		if (!tmpChallenge || !tmpChallenge.ChallengeToken)
		{
			this.fable.AppData.Auth.LastError = 'This sign-in request is no longer valid. Please start again.';
			return Promise.resolve(false);
		}
		return this._fetchJSON('Login/Recovery',
			{
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ ChallengeToken: tmpChallenge.ChallengeToken, RecoveryCode: pCode })
			})
			.then((pBody) =>
			{
				if (pBody && pBody.LoggedIn && pBody.UserRecord)
				{
					this.fable.AppData.Auth.LoggedIn = true;
					this.fable.AppData.Auth.User = pBody.UserRecord;
					this.fable.AppData.Auth.PendingChallenge = null;
					return true;
				}
				this.fable.AppData.Auth.LastError = (pBody && pBody.Error) || 'That recovery code is not valid.';
				return false;
			})
			.catch((pErr) =>
			{
				this.fable.AppData.Auth.LastError = pErr.message;
				return false;
			});
	}

	// Re-send a fresh code on the pending challenge. Resolves to the masked
	// destination it went to, or false.
	resendLoginCode()
	{
		this.initializeAppData();
		let tmpChallenge = this.fable.AppData.Auth.PendingChallenge;
		if (!tmpChallenge || !tmpChallenge.ChallengeToken) { return Promise.resolve(false); }
		return this._fetchJSON('Login/Resend',
			{
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ ChallengeToken: tmpChallenge.ChallengeToken })
			})
			.then((pBody) => (pBody && pBody.Resent) ? (pBody.MaskedDestination || tmpChallenge.MaskedDestination) : false)
			.catch(() => false);
	}

	cancelChallenge()
	{
		this.initializeAppData();
		this.fable.AppData.Auth.PendingChallenge = null;
		this.fable.AppData.Auth.LastError = null;
	}

	signup(pPartial)
	{
		this.initializeAppData();
		this.fable.AppData.Auth.LastError = null;
		return this._fetchJSON('Signup',
			{
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify(pPartial)
			})
			.then((pBody) =>
			{
				if (pBody && pBody.Success && pBody.UserRecord)
				{
					this.fable.AppData.Auth.LoggedIn = true;
					this.fable.AppData.Auth.User = pBody.UserRecord;
					return true;
				}
				this.fable.AppData.Auth.LastError = (pBody && pBody.Error) || 'Signup failed.';
				return false;
			})
			.catch((pErr) =>
			{
				this.fable.AppData.Auth.LastError = pErr.message;
				return false;
			});
	}

	logout()
	{
		this.initializeAppData();
		return this._fetchJSON('Deauthenticate', { method: 'POST' })
			.then(() =>
			{
				this.fable.AppData.Auth.LoggedIn = false;
				this.fable.AppData.Auth.User = null;
				return true;
			})
			.catch((pErr) =>
			{
				this.fable.AppData.Auth.LastError = pErr.message;
				return false;
			});
	}

	/**
	 * Returns the URL to redirect the browser to in order to begin an OAuth
	 * flow with the named provider. Caller does `window.location.href = url`
	 * (or lets the browser follow a plain anchor href).
	 *
	 * pReturnTo (optional) is a same-origin relative path (e.g. the SPA's
	 * current "/#/board" route) the server sends the browser back to once the
	 * provider round trip completes, instead of the app's default landing
	 * page. See orator-authentication's _safeReturnTo -- only a leading-slash
	 * relative path is honored, so this can never become an open redirect.
	 */
	beginURL(pProviderName, pReturnTo)
	{
		let tmpURL = this._url('OAuth/Begin/' + encodeURIComponent(pProviderName));
		if (pReturnTo) { tmpURL += '?returnTo=' + encodeURIComponent(pReturnTo); }
		return tmpURL;
	}
}

module.exports = PictProviderAuthClient;
module.exports.default_configuration = _DefaultProviderConfiguration;
